import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { obtenerMisTokensIA, obtenerMisCreditosIA } from './api/historialIA';
import "./TokenFab.css";

function formatCreditos(n) {
    const v = Number(n || 0);
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${Math.round(v / 1_000)}k`;
    return `${v}`;
}

function formatTokens(n) {
    const v = Number(n || 0);
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
    return `${v}`;
}

function todayKey() {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Santiago",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(new Date()); // "YYYY-MM-DD"
}

export default function TokenFab() {
    const [open, setOpen] = useState(false);
    const [uid, setUid] = useState(null);
    const [stats, setStats] = useState(null);
    const [creditos, setCreditos] = useState(null);
    const [err, setErr] = useState("");

    // 1) uid del usuario logueado
    useEffect(() => {
        const auth = getAuth();
        const unsub = onAuthStateChanged(auth, (u) => {
            setUid(u?.uid ?? null);
        });
        return () => unsub();
    }, []);

    // 2) leer stats de consumo (Postgres) — sin listener: se refresca al
    // montar y cada vez que se abre el panel (no hay push nativo).
    const cargarStats = async () => {
        if (!uid) { setStats(null); setCreditos(null); setErr(""); return; }
        setErr("");
        try {
            const [statsRes, creditosRes] = await Promise.all([
                obtenerMisTokensIA(),
                obtenerMisCreditosIA().catch(() => null),
            ]);
            setStats(statsRes);
            setCreditos(creditosRes);
        } catch (e) {
            console.error("TokenFab error:", e);
            setErr("No pude leer tus tokens.");
        }
    };

    useEffect(() => { cargarStats(); }, [uid]);
    useEffect(() => { if (open) cargarStats(); }, [open]);

    const day = todayKey();

    // Tokens de hoy — Claude guarda input y output por separado
    const inputHoy = stats?.byDay?.[day]?.input ?? 0;
    const outputHoy = stats?.byDay?.[day]?.output ?? 0;
    const tokensHoy = inputHoy + outputHoy;
    const callsHoy = stats?.byDay?.[day]?.calls ?? 0;

    // Totales históricos
    const totalInput = stats?.totalInputTokens ?? 0;
    const totalOutput = stats?.totalOutputTokens ?? 0;
    const totalTokens = totalInput + totalOutput;
    const totalCalls = stats?.totalCalls ?? 0;

    // Última llamada
    const lastModel = stats?.lastModel ?? "";
    const lastLatencyMs = stats?.lastLatencyMs ?? null;

    // Badge: créditos restantes si hay bolsa finita, si no tokens de hoy
    const tieneBolsaFinita = creditos && creditos.ilimitado === false;
    const badge = tieneBolsaFinita ? formatCreditos(creditos.creditosRestantes) : formatTokens(tokensHoy);
    const creditosBajos = tieneBolsaFinita && creditos.creditosRestantes <= 0;

    if (!uid) return null;

    return (
        <>
            <div className="tokenfab-wrap">
                <button
                    type="button"
                    className={`tokenfab-btn${creditosBajos ? ' tokenfab-btn--bajo' : ''}`}
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Ver créditos y consumo de IA"
                    title="Ver créditos y consumo de IA"
                >
                    <span className="tokenfab-icon" aria-hidden="true">{tieneBolsaFinita ? '🪙' : '🧮'}</span>
                    <span className={`tokenfab-badge${creditosBajos ? ' tokenfab-badge--bajo' : ''}`}>{badge}</span>
                </button>

                <div className="tokenfab-tooltip">
                    {tieneBolsaFinita ? (
                        <>Créditos IA: <b>{formatCreditos(creditos.creditosRestantes)}</b></>
                    ) : (
                        <>Tokens hoy: <b>{formatTokens(tokensHoy)}</b></>
                    )}
                    <div style={{ opacity: 0.8, marginTop: 4 }}>Click para detalle</div>
                </div>
            </div>

            {open && (
                <div className="tokenfab-panel" role="dialog" aria-label="Créditos y consumo de IA">
                    <div className="tokenfab-panel-head">
                        <div className="tokenfab-panel-title">Créditos de IA</div>
                        <button
                            type="button"
                            className="tokenfab-close"
                            onClick={() => setOpen(false)}
                            aria-label="Cerrar"
                        >✕</button>
                    </div>

                    {/* Bolsa de créditos — siempre visible arriba, aplica a todas las
                        consultas de IA (Asesor, PM, Comparador, Mis proyectos). */}
                    {creditos && (
                        <div className={`tokenfab-creditos${creditosBajos ? ' tokenfab-creditos--bajo' : ''}`}>
                            <div className="tokenfab-creditos-label">
                                {creditos.ilimitado ? 'Créditos IA' : creditos.organizationNombre ? `Créditos de ${creditos.organizationNombre}` : 'Créditos IA disponibles'}
                            </div>
                            <div className="tokenfab-creditos-valor">
                                {creditos.ilimitado ? '∞ Ilimitado' : formatCreditos(creditos.creditosRestantes)}
                            </div>
                            {!creditos.ilimitado && creditos.organizationNombre && (
                                <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Compartidos con tu organización</div>
                            )}
                            {creditosBajos && (
                                <div className="tokenfab-creditos-aviso">Sin créditos — contacta al administrador para recargar.</div>
                            )}
                        </div>
                    )}

                    {err ? (
                        <div className="tokenfab-error">{err}</div>
                    ) : !stats ? (
                        <div className="tokenfab-hint">Sin actividad registrada aún.</div>
                    ) : (
                        <>
                            {/* Hoy */}
                            <div className="tokenfab-subtitle">Hoy</div>
                            <div className="tokenfab-row">
                                <div className="tokenfab-label">Total tokens</div>
                                <div className="tokenfab-value">
                                    {formatTokens(tokensHoy)}{" "}
                                    <span style={{ opacity: 0.6 }}>({callsHoy} llamadas)</span>
                                </div>
                            </div>
                            <div className="tokenfab-row">
                                <div className="tokenfab-label">Input</div>
                                <div className="tokenfab-value">{formatTokens(inputHoy)}</div>
                            </div>
                            <div className="tokenfab-row">
                                <div className="tokenfab-label">Output</div>
                                <div className="tokenfab-value">{formatTokens(outputHoy)}</div>
                            </div>

                            <div className="tokenfab-divider" />

                            {/* Histórico */}
                            <div className="tokenfab-subtitle">Total histórico</div>
                            <div className="tokenfab-row">
                                <div className="tokenfab-label">Total tokens</div>
                                <div className="tokenfab-value">
                                    {formatTokens(totalTokens)}{" "}
                                    <span style={{ opacity: 0.6 }}>({totalCalls} llamadas)</span>
                                </div>
                            </div>
                            <div className="tokenfab-row">
                                <div className="tokenfab-label">Input</div>
                                <div className="tokenfab-value">{formatTokens(totalInput)}</div>
                            </div>
                            <div className="tokenfab-row">
                                <div className="tokenfab-label">Output</div>
                                <div className="tokenfab-value">{formatTokens(totalOutput)}</div>
                            </div>

                            <div className="tokenfab-divider" />

                            {/* Última llamada */}
                            <div className="tokenfab-subtitle">Última llamada</div>
                            {lastModel ? (
                                <div className="tokenfab-row">
                                    <div className="tokenfab-label">Modelo</div>
                                    <div className="tokenfab-value">{lastModel}</div>
                                </div>
                            ) : null}
                            {lastLatencyMs != null ? (
                                <div className="tokenfab-row">
                                    <div className="tokenfab-label">Latencia</div>
                                    <div className="tokenfab-value">{lastLatencyMs} ms</div>
                                </div>
                            ) : null}

                            <div className="tokenfab-hint">* Solo ves tu consumo personal.</div>
                        </>
                    )}
                </div>
            )}
        </>
    );
}